import { MAX_ROW_LIMIT, buildResource, runQueries } from '../GscTools/query';
import {
    COMPARISON_DAYS,
    HOURLY_DATA_STATE,
    HOURLY_SEARCH_TYPE,
    HOUR_DIMENSION,
    requestDates,
    todayInReportingZone,
} from './hours';

/**
 * The one call this report makes.
 *
 * Both windows go in it together. That is the opposite of how the other GSC tools are wired, and
 * for a reason: a 24-hour comparison is a single claim — these hours against the same hours a
 * week ago — and half of it is not a weaker version of it, it is a different report with the same
 * heading. Twenty-four rows a side also costs nothing, so there is no benefit to splitting them.
 *
 * The bounds are two Pacific calendar days per window, which is the smallest range that can
 * contain any 24-hour span, and the exact hours are cut from the response afterwards. The API
 * takes dates; the window this report wants is a run of hours that does not line up with either
 * end of them.
 *
 * `hourly_all` is the data state that returns hourly rows at all, and it returns the
 * still-collecting ones along with the settled ones. There is no "final" hourly state to ask for
 * instead — which is precisely why the completeness boundary has to be drawn by this report and
 * labelled as its own.
 */
export async function fetchHours({ credentialsDomainId, scope, now = new Date() }) {
    const today = todayInReportingZone(now);

    const hourly = (dates) =>
        buildResource({
            period: dates,
            dimensions: [HOUR_DIMENSION],
            aggregationType: 'auto',
            dataState: HOURLY_DATA_STATE,
            searchType: HOURLY_SEARCH_TYPE,
            rowLimit: MAX_ROW_LIMIT,
        });

    const queries = [{ key: 'current', resource: hourly(requestDates(today)) }];

    // Left out rather than sent and ignored: "no comparison" should cost nothing, and a baseline
    // fetched behind the user's back would show up in the request budget they are being charged.
    if (scope.comparing) {
        queries.push({
            key: 'baseline',
            resource: hourly(requestDates(today, COMPARISON_DAYS)),
        });
    }

    const results = await runQueries({
        credentialsDomainId,
        siteUrl: scope.siteUrl,
        maxCount: MAX_ROW_LIMIT,
        queries,
    });

    return { current: results.current ?? [], baseline: results.baseline ?? [] };
}
